import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  TemplateRef,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core'
import { ControlContainer, NgForm } from '@angular/forms'
import { UiSize } from '../../models/ui.model'
import { Subject } from 'rxjs'

@Component({
  selector: 'op-select-box-ng',
  templateUrl: './select-box-ng.component.html',
  styleUrls: ['./select-box-ng.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class SelectBoxNgComponent<T, K = T>
  implements OnInit, OnDestroy, OnChanges
{
  @Input() placeholder?: string
  @Input() class?: string
  @Input({ required: true }) key!: string
  @Input() value?: K
  @Input() dataSource?: T[]
  @Input() size?: UiSize
  @Input() isLoading = false

  // Lazy loading support
  @Input() lazyLoad?: boolean = false
  @Input() loadFunction?: (
    skip: number,
    take: number,
    search?: string,
  ) => Promise<{ data: T[]; total: number }>
  @Input() pageSize = 50

  // Templates
  @Input() displayParser?: (item: T) => string
  @Input() itemTemplate?: TemplateRef<{ $implicit: T }>

  // Search params
  @Input() keyField: string | ((item: T) => void) = '_id'
  @Input() searchField = 'name'
  @Input() displayField = 'name'

  // Style
  @Input() dropdownButton = true
  @Input() clearButton = true
  @Input() disabled = false
  @Input() stylingMode: 'outlined' | 'filled' | 'underlined' = 'outlined'

  // Errors
  @Input() required = false
  @Input() requiredError?: string

  @Input() resetTrigger?: unknown

  @ViewChild('dropdown') dropdown?: ElementRef

  public selectClasses = ''
  public loadedOptions: T[] = []
  public hasMore = true
  private currentPage = 0
  private searchTerm = ''
  private panelWrapper: HTMLElement | null = null
  private destroy$ = new Subject<void>()

  @Output() readonly valueChange = new EventEmitter<K | undefined>()

  ngOnInit() {
    const selectClasses: string[] = []

    // Size
    if (this.size) {
      selectClasses.push(`form-control-${this.size}`)
    }

    this.selectClasses = selectClasses.join(' ')

    // If lazy loading, initialize with first page
    if (this.lazyLoad && this.loadFunction) {
      this.loadMore()
    } else if (Array.isArray(this.dataSource)) {
      this.loadedOptions = this.dataSource
    }
  }

  ngOnDestroy() {
    this.onHide()
    this.destroy$.next()
    this.destroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetTrigger'] && !changes['resetTrigger'].firstChange) {
      // Reset the lazy load state
      this.loadedOptions = []
      this.currentPage = 0
      this.hasMore = true
      this.searchTerm = ''

      // Reload data if lazy loading
      if (this.lazyLoad && this.loadFunction) {
        this.loadMore()
      }
    }
  }

  async loadMore() {
    if (!this.lazyLoad && Array.isArray(this.dataSource)) {
      this.loadedOptions = this.dataSource
      return
    }

    if (!this.loadFunction || this.isLoading || !this.hasMore) {
      return
    }

    this.isLoading = true
    try {
      const result = await this.loadFunction(
        this.currentPage * this.pageSize,
        this.pageSize,
        this.searchTerm,
      )

      this.loadedOptions = [...this.loadedOptions, ...result.data]
      this.hasMore = this.loadedOptions.length < result.total
      this.currentPage++
    } finally {
      this.isLoading = false
    }
  }
  private get wrapper(): HTMLElement | null {
    return document.querySelector(
      `.op-select-panel-${this.key} .p-dropdown-items-wrapper`,
    )
  }

  onShow() {
    if (!this.lazyLoad) return
    if (this.loadedOptions.length === 0) void this.loadMore()

    setTimeout(() => {
      this.panelWrapper = document.querySelector(
        `.op-select-panel-${this.key} .p-dropdown-items-wrapper`,
      )
      this.panelWrapper?.addEventListener('scroll', this.handlePanelScroll)
    })
  }

  onHide() {
    this.panelWrapper?.removeEventListener('scroll', this.handlePanelScroll)
    this.panelWrapper = null
  }

  private handlePanelScroll = (event: Event) => {
    const el = event.target as HTMLElement
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
      void this.loadMore()
    }
  }

  onFilter(event: { filter: string }) {
    if (!this.lazyLoad || !this.loadFunction) return

    this.searchTerm = event.filter
    this.currentPage = 0
    this.loadedOptions = []
    this.hasMore = true
    this.loadMore()
  }

  checkValue(value: K | null) {
    return value === null ? undefined : value
  }

  getKeyField(): string {
    return typeof this.keyField === 'string' ? this.keyField : '_id'
  }

  get optionsArray(): T[] {
    if (this.lazyLoad) {
      return Array.isArray(this.loadedOptions) ? this.loadedOptions : []
    }
    return Array.isArray(this.dataSource) ? this.dataSource : []
  }
}
